import React from "react";
import {
  View,
  StyleSheet,
  Animated,
  PanResponder,
  TouchableOpacity,
  Platform,
} from "react-native";
import { useNavigation } from "@react-navigation/native";
import { StackNavigationProp } from "@react-navigation/stack";
import { renderLinks, getCardStyle } from "../utils/renderLinks";
import { useTranslation } from "../hooks/useTranslation";
import CustomText from "./CustomText";

type RootStackParamList = {
  NameDetails: { nameDetails: CardData };
};

type CardData = {
  id: string;
  nombre: string;
  significado_extendido: string;
  significado_resumido: string;
  personajes_famosos: string[];
  ranking: number;
  sex: string;
};

const SwipeCard = ({
  pan,
  opacity,
  cardData,
  onSwipe,
  isMaleTurn,
}: {
  pan: Animated.ValueXY;
  opacity: Animated.Value;
  cardData: CardData;
  onSwipe: (direction: "left" | "right") => void;
  isMaleTurn: boolean;
}) => {
  const navigation = useNavigation<StackNavigationProp<RootStackParamList>>();
  const { getTranslation } = useTranslation();

  const scale = React.useRef(new Animated.Value(1)).current; // Scale value for the card

  const panResponder = React.useRef(
    PanResponder.create({
      onMoveShouldSetPanResponder: () => true,
      onPanResponderMove: Animated.event([null, { dx: pan.x, dy: pan.y }], {
        useNativeDriver: false,
      }),
      onPanResponderGrant: () => {
        // Scale up slightly when the user starts swiping
        Animated.spring(scale, {
          toValue: 1.1,
          useNativeDriver: false,
        }).start();
      },
      onPanResponderRelease: (_, gestureState) => {
        const threshold = Platform.OS === "ios" ? 110 : 120;

        if (gestureState.dx > threshold) {
          onSwipe("right");
          Animated.parallel([
            Animated.timing(pan, {
              toValue: { x: 500, y: 0 },
              duration: 200,
              useNativeDriver: false,
            }),
            Animated.timing(opacity, {
              toValue: 0,
              duration: 200,
              useNativeDriver: false,
            }),
          ]).start(() => {
            pan.setValue({ x: 0, y: 0 });
            opacity.setValue(1);
            scale.setValue(1); // Reset scale to 1
          });
        } else if (gestureState.dx < -threshold) {
          onSwipe("left");
          Animated.parallel([
            Animated.timing(pan, {
              toValue: { x: -500, y: 0 },
              duration: 200,
              useNativeDriver: false,
            }),
            Animated.timing(opacity, {
              toValue: 0,
              duration: 200,
              useNativeDriver: false,
            }),
          ]).start(() => {
            pan.setValue({ x: 0, y: 0 });
            opacity.setValue(1);
            scale.setValue(1); // Reset scale to 1
          });
        } else {
          Animated.parallel([
            Animated.spring(pan, {
              toValue: { x: 0, y: 0 },
              useNativeDriver: false,
            }),
            Animated.spring(scale, {
              toValue: 1,
              useNativeDriver: false,
            }),
          ]).start();
        }
      },
    })
  ).current;

  return (
    <View style={styles.container}>
      <Animated.View
        {...panResponder.panHandlers}
        style={[
          styles.card,
          getCardStyle(isMaleTurn),
          {
            transform: [
              ...pan.getTranslateTransform(),
              { scale }, // Apply scale transformation
            ],
            opacity,
          },
        ]}
      >
        <CustomText style={styles.title}>{cardData.nombre}</CustomText>
        <CustomText style={styles.description}>
          {renderLinks(cardData.significado_resumido)}
        </CustomText>
        <CustomText style={styles.description}>
          {renderLinks(cardData.significado_extendido)}
        </CustomText>
        <View style={styles.buttonContainer}>
          <TouchableOpacity
            style={styles.button}
            activeOpacity={0.7}
            onPress={() =>
              navigation.navigate("NameDetails", {
                nameDetails: cardData,
              })
            }
          >
            <CustomText style={styles.buttonText}>
              {getTranslation("ID_MORE_INFO")}
            </CustomText>
          </TouchableOpacity>
        </View>
      </Animated.View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  card: {
    width: "85%",
    height: "100%",
    backgroundColor: "#f8f9fa",
    borderRadius: 20,
    padding: 16,
    justifyContent: "space-between",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.3,
    shadowRadius: 6,
    elevation: 5,
  },
  title: {
    fontFamily: "Urbanist_900Black",
    fontSize: 60,
    fontWeight: "900",
    textAlign: "center",
    marginBottom: 10,
    color: "#000",
  },
  description: {
    fontSize: 16,
    textAlign: "left",
    color: "#374151",
    marginBottom: 10,
  },
  buttonContainer: {
    marginTop: 20,
    alignItems: "center",
  },
  button: {
    backgroundColor: "#3B82F6",
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 12,
    alignItems: "center",
  },
  buttonText: {
    fontFamily: "Urbanist_400Regular",
    fontSize: 16,
    color: "#fff",
    textAlign: "center",
  },
});

export default SwipeCard;
